import { PrincipalIdSchema } from "@core/domains/identity";
import { identity } from "@core/foundation/function";
import {
  createModel,
  createModelId,
  createModelSchemas,
} from "@core/foundation/model";
import { singleline } from "@core/foundation/string";
import { z } from "@core/zod";

import { ProjectIdSchema } from "./project.ts";
import { ID_PREFIX } from "./shared/id-prefix.ts";
import { RevisionContentSchema } from "./shared/revision-content.ts";
import { RevisionIdSchema } from "./shared/revision-id.ts";

const { generateId: generateMemoryId, idSchema: MemoryIdSchema } =
  createModelId({ idPrefix: ID_PREFIX.MEMORY });

export { MemoryIdSchema };

export const Memory = createModel({
  create: (item, schema) => {
    return { ...schema.decode(item), id: generateMemoryId() };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        content: RevisionContentSchema,
        id: MemoryIdSchema,
        lastStatusChange: z
          .discriminatedUnion("kind", [
            z.object(
              { kind: z.literal("user") },
              { kind: "The maker of the change, the memory's owner." },
            ),
            z.object(
              { kind: z.literal("agent"), reason: z.string() },
              {
                kind: "The maker of the change, an agent.",
                reason: "The reason the agent gave for the change.",
              },
            ),
          ])
          .nullable(),
        principalId: PrincipalIdSchema,
        projectId: ProjectIdSchema,
        revisionId: RevisionIdSchema,
        status: z.enum(["active", "archived"]),
        title: z.string(),
      },
      {
        content: singleline`
          The memory's current content, the one its newest revision holds, or a
          placeholder on a memory stored before the field until the backfill
          writes it.
        `,
        id: "The unique identifier for the memory.",
        lastStatusChange: singleline`
          The memory's last status change, made by its owner or by an agent
          giving its reason, or null where none is recorded.
        `,
        principalId:
          "The identifier of the principal associated with the memory.",
        projectId: "The identifier of the project associated with the memory.",
        revisionId: singleline`
          The id of the memory's newest revision, or a placeholder on a memory
          stored before the field until the backfill writes it.
        `,
        status: "The status of the memory, either active or archived.",
        title: "The title of the memory.",
      },
    ),
    defaults: {
      content: { kind: "text", text: "placeholder" },
      revisionId: `${ID_PREFIX.REVISION}placeholder`,
      status: "active",
    },
    derive: {
      create: schema => schema.omit({ id: true }),
      recovery: identity,
      update: schema =>
        schema
          .pick({
            content: true,
            lastStatusChange: true,
            revisionId: true,
            status: true,
          })
          .partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface Memory extends z.infer<typeof Memory.schema> {}
